/**
 * UpdateNotification Component
 *
 * Displays a non-intrusive banner when a new application version is available.
 * Uses the useVersionCheck hook to detect updates and provides user-friendly
 * options to update immediately or dismiss for current session.
 *
 * Features:
 * - Beautiful gradient banner with smooth animations
 * - Shows version information
 * - Non-blocking (users can continue working)
 * - Session-based dismissal (won't nag repeatedly)
 * - Accessible with proper ARIA labels
 */

import { useVersionCheckContext } from '../../context/VersionCheckContext';
import { useI18n } from '../../context/I18nContext';
import './UpdateNotification.css';

export const UpdateNotification = () => {
  const {
    updateAvailable,
    newVersion,
    currentVersion,
    newBuildTime,
    applyUpdate,
    dismissUpdate,
    securityState,
  } = useVersionCheckContext();
  const { t } = useI18n();

  // Mutually exclusive with the security flow: whenever the security gate
  // is active (soft / hard / locked) or still being verified, suppress the
  // legacy banner. The security UI is authoritative; double-prompting is
  // confusing and was explicit user-rejected behavior. Only show the legacy
  // banner when securityState === 'none'.
  if (securityState !== 'none') {
    return null;
  }

  // Don't render anything if no update is available
  if (!updateAvailable) {
    return null;
  }

  // Format build time for display
  const formatBuildTime = (buildTime) => {
    if (!buildTime) return '';
    try {
      const date = new Date(buildTime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (e) {
      return '';
    }
  };

  return (
    <div
      className="update-notification"
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="update-notification__content">
        {/* Icon */}
        <div className="update-notification__icon" aria-hidden="true">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10"></polyline>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
          </svg>
        </div>

        {/* Text Content */}
        <div className="update-notification__text">
          <strong className="update-notification__title">
            {t('UpdateNotification.title', {}, 'New version available!')}
          </strong>
          <p className="update-notification__description">
            {t(
              'UpdateNotification.versionReady',
              { version: newVersion },
              `Version ${newVersion} is ready`,
            )}
            {newBuildTime && ` (${t('UpdateNotification.built', {}, 'built')} ${formatBuildTime(newBuildTime)})`}.{' '}
            {t('UpdateNotification.updatePrompt', {}, 'Update now for the latest features and fixes.')}
            {currentVersion && (
              <>
                <br />
                <span className="update-notification__current-version">
                  {t(
                    'UpdateNotification.currentVersion',
                    { version: currentVersion },
                    `Current: v${currentVersion}`,
                  )}
                </span>
              </>
            )}
          </p>
        </div>

        {/* Actions */}
        <div className="update-notification__actions">
          <button
            className="update-notification__button update-notification__button--primary"
            onClick={applyUpdate}
            aria-label={t('UpdateNotification.updateAriaLabel', {}, 'Update application now')}
          >
            {t('UpdateNotification.updateNow', {}, 'Update Now')}
          </button>
          <button
            className="update-notification__button update-notification__button--secondary"
            onClick={dismissUpdate}
            aria-label={t('UpdateNotification.dismissAriaLabel', {}, 'Dismiss update notification')}
          >
            {t('UpdateNotification.later', {}, 'Later')}
          </button>
        </div>
      </div>
    </div>
  );
};
